 
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPlus as faPlusSolid, faSave, faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
// eslint-disable-next-line no-restricted-imports
import Dialog from '@mui/material/Dialog';
// eslint-disable-next-line no-restricted-imports
import DialogActions from '@mui/material/DialogActions';
// eslint-disable-next-line no-restricted-imports
import DialogContent from '@mui/material/DialogContent';
// eslint-disable-next-line no-restricted-imports
import DialogTitle from '@mui/material/DialogTitle';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'src/components/Accordion';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card, CardContent, CardHeader } from 'src/components/Card';
import { DndSortableList } from 'src/components/DndSortableList';
import { EditButton } from 'src/components/EditButton';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { InputAdornment } from 'src/components/InputAdornment';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { Platform, ProfileMenuItem } from 'src/models/platform/_types';
import { updatePlatform } from 'src/models/platform/platform';
import {
  generatePredefinedTabs,
  hostOrSubdomain,
  yupValidateAppUrl,
  yupValidateAppVersion,
} from 'src/models/platform/utils';
import { isAdmin } from 'src/models/user/utils';
import { EditableFieldWrapper } from 'src/shared/EditableFieldWrapper';
import { FormAutocomplete } from 'src/shared/Forms/Fields/FormAutocomplete';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { GroupSelect } from 'src/shared/Forms/Selects/GroupSelect';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';


const tabVisibilityOptions = [
  { id: 'trainer', title: 'Staff' },
  { id: 'client', title: 'Client Stage' },
  { id: 'group', title: 'Group Members' },
  { id: 'product', title: 'Product Purchasers' },
  { id: 'workout_plan', title: 'Plan Purchasers' },
  { id: 'user_packages', title: 'Users with an Active Package' },
  {
    id: 'no_active_subscription',
    title: 'Client Stage with NO Active Subscription',
  },
  { id: 'all_non_staff', title: 'All Non-Staff Accounts' },
  { id: 'all', title: 'All Accounts' },
];

const platformOptions = [
  { id: '', title: 'All' },
  { id: 'android', title: 'Android' },
  { id: 'ios', title: 'iOS' },
];

// Validation schema for menu items
const menuItemSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  url: Yup.string().test('valid-url', 'Invalid URL', yupValidateAppUrl),
  minVersion: Yup.string().test(
    'valid-min-version',
    'Invalid min version',
    yupValidateAppVersion
  ),
  maxVersion: Yup.string().test(
    'valid-max-version',
    'Invalid max version',
    yupValidateAppVersion
  ),
  icon: Yup.string().matches(
    /^fa-[^\s]+$/,
    'Icon must start with fa- and contain no spaces'
  ),
});

const profileMenuSchema = Yup.object().shape({
  profileMenu: Yup.array().of(menuItemSchema),
});

interface CustomProfileMenuEditorProps {
  platform: Platform;
  defaultExpanded?: boolean;
}

// Define a type compatible with DndSortableList requirements
interface SortableProfileMenuItem extends Omit<ProfileMenuItem, 'id'> {
  id: string | number;
}

export function CustomProfileMenuEditor({
  platform,
  defaultExpanded = false,
}: CustomProfileMenuEditorProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'customProfileMenuEditor',
  });
  const { user } = useOptionalSession();
  const queryClient = useQueryClient();
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [isPresetDialogOpen, setIsPresetDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const predefinedTabs = generatePredefinedTabs(platform);

  // Force re-render when platform data is loaded
  // This ensures state is initialized with the latest platform data
  const key = platform?.id
    ? `profile-menu-${platform.id}`
    : 'profile-menu-loading';

  const { mutate: updatePlatformMutation } = useMutation({
    mutationFn: (params: { id: string; profileMenu: ProfileMenuItem[] }) =>
      updatePlatform(params.id, { profileMenu: params.profileMenu }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', platform.id] });
      toast.success(t('updateSuccess'));
      setEditingItemIndex(null);
    },

    onError: (error) => {
      toast.error(api.formatError(error, t('updateError')));
    },
  });

  // Prepare items for DndSortableList by ensuring each has an id
  const prepareSortableItems = (
    items: ProfileMenuItem[]
  ): SortableProfileMenuItem[] => {
    return items.map((item, index) => ({
      ...item,
      id: item.id?.toString() || `profile-item-${index}`,
    }));
  };

  // Convert back from sortable items to regular items
  const fromSortableItems = (
    items: SortableProfileMenuItem[]
  ): ProfileMenuItem[] => {
    return items.map((item) => ({
      ...item,
      id:
        typeof item.id === 'string' && /^\d+$/.test(item.id)
          ? parseInt(item.id, 10)
          : typeof item.id === 'number'
          ? item.id
          : undefined,
    }));
  };

  const initialValues = {
    profileMenu: platform?.profileMenu || [],
  };

  const handleSubmit = (values, { setSubmitting }) => {
    if (!platform?.id) return;

    // Set title to match name for all items and transform visibility
    const itemsWithTitle = values.profileMenu.map((item) => ({
      ...item,
      title: item.name,
      visibility: Array.isArray(item.visibility)
        ? item.visibility.map((opt) => (typeof opt === 'string' ? opt : opt.id))
        : item.visibility,
    }));

    updatePlatformMutation(
      {
        id: platform?.id?.toString() || '',
        profileMenu: itemsWithTitle,
      },
      {
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  };

  const handlePresetSelection = (preset, { values, setValues }) => {
    const updatedMenu = [
      ...values.profileMenu,
      {
        name: preset.name,
        title: preset.name,
        url: preset.url || `https://${hostOrSubdomain(platform)}/`,
        icon: preset.icon,
        minVersion: '',
        maxVersion: '',
        platform: '',
        groupId: '',
        visibility: preset.visibility || [
          'trainer',
          'client',
          'group',
          'product',
          'workout_plan',
          'user_packages',
          'no_active_subscription',
          'all_non_staff',
          'all',
        ],
      },
    ];

    setValues({ profileMenu: updatedMenu });
    setEditingItemIndex(updatedMenu.length - 1); // Start editing the new item
    setIsPresetDialogOpen(false);
  };

  const handleAddMenuItem = () => {
    setSearchQuery('');
    setIsPresetDialogOpen(true);
  };

  const handleAddEmptyMenuItem = (values, { setValues }) => {
    const updatedMenu = [
      ...values.profileMenu,
      {
        name: '',
        title: '',
        url: `https://${hostOrSubdomain(platform)}/`,
        icon: 'fa-home',
        minVersion: '',
        maxVersion: '',
        platform: '',
        groupId: '',
        visibility: [
          'trainer',
          'client',
          'group',
          'product',
          'workout_plan',
          'user_packages',
          'no_active_subscription',
          'all',
        ],
      },
    ];

    setValues({ profileMenu: updatedMenu });
    setEditingItemIndex(updatedMenu.length - 1);
    setIsPresetDialogOpen(false);
  };

  const handleRemoveMenuItem = (index: number, { values, setValues }) => {
    const updatedMenu = [...values.profileMenu];
    updatedMenu.splice(index, 1);
    setValues({ profileMenu: updatedMenu });
  };

  // Filter predefined tabs based on search query
  const filteredTabs = searchQuery
    ? predefinedTabs.filter(
        (tab) =>
          tab.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tab.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : predefinedTabs;

  return (
    <Accordion
      key={key}
      label="Customize Profile Menu"
      defaultExpanded={defaultExpanded}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={profileMenuSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setValues, isSubmitting, resetForm, submitForm }) => (
          <Form>
            <StickyCard
              key={'custom-profile-menu'}
              title={'Customize Profile Menu'}
              actions={
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  startIcon={
                    <Icon icon={faSave} />
                  }
                >
                  {t('saveProfileMenu')}
                </Button>
              }
            >
              <Stack spacing={4}>
                {/* Menu Items */}
                <DndSortableList
                  items={prepareSortableItems(values.profileMenu)}
                  setItems={(newItems) => {
                    // Handle both function and direct value updates
                    const updatedItems =
                      typeof newItems === 'function'
                        ? newItems(prepareSortableItems(values.profileMenu))
                        : newItems;

                    const updatedMenu = fromSortableItems(updatedItems);
                    setValues({
                      profileMenu: updatedMenu,
                    });
                  }}
                  sx={{ p: 0 }}
                  disabled={editingItemIndex !== null}
                >
                  {(item) => {
                    // Get the index that corresponds to the original array
                    const index = values.profileMenu.findIndex(
                      (menuItem, idx) => {
                        // First try to match by ID
                        if (menuItem.id && item.id) {
                          return menuItem.id.toString() === item.id.toString();
                        }
                        // Fall back to matching by position in the DnD list
                        if (item.id?.toString()?.startsWith('profile-item-')) {
                          const itemIndex = parseInt(
                            item.id.toString().replace('profile-item-', ''),
                            10
                          );
                          return idx === itemIndex;
                        }
                        return false;
                      }
                    );

                    return (
                      <Card
                        key={index}
                        sx={{ borderRadius: 2, overflow: 'visible' }}
                      >
                        <CardHeader
                          title={item.name || t('newItem')}
                          action={
                            <Stack direction="row" spacing={1}>
                              {editingItemIndex === index && (
                                <Button
                                  color="error"
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleRemoveMenuItem(index, {
                                      values,
                                      setValues,
                                    })
                                  }
                                  startIcon={
                                    <Icon
                                      icon={faTrash}
                                    />
                                  }
                                >
                                  {t('removeItem')}
                                </Button>
                              )}
                              <EditButton
                                editing={editingItemIndex === index}
                                onClickEdit={() => setEditingItemIndex(index)}
                                onClickSave={() => {
                                  submitForm();
                                }}
                                onClickCancel={() => {
                                  setEditingItemIndex(null);
                                  resetForm();
                                }}
                                saving={isSubmitting}
                              />
                            </Stack>
                          }
                        />
                        <CardContent>
                          <Stack spacing={3}>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 4 }}>
                                <EditableFieldWrapper
                                  label={t('name')}
                                  editing={editingItemIndex === index}
                                  value={item.name}
                                >
                                  <FormTextField
                                    name={`profileMenu[${index}].name`}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      const updatedMenu = [
                                        ...values.profileMenu,
                                      ];
                                      updatedMenu[index] = {
                                        ...updatedMenu[index],
                                        name: newValue,
                                        title: newValue, // Also update title to match
                                      };
                                      setValues({
                                        ...values,
                                        profileMenu: updatedMenu,
                                      });
                                    }}
                                    fullWidth
                                  />
                                </EditableFieldWrapper>
                              </Grid>
                              <Grid size={{ xs: 12, md: 4 }}>
                                <EditableFieldWrapper
                                  label={t('url')}
                                  editing={editingItemIndex === index}
                                  value={item.url}
                                >
                                  <FormTextField
                                    name={`profileMenu[${index}].url`}
                                    onChange={(e) => {
                                      const updatedMenu = [
                                        ...values.profileMenu,
                                      ];
                                      updatedMenu[index] = {
                                        ...updatedMenu[index],
                                        url: e.target.value,
                                      };
                                      setValues({ profileMenu: updatedMenu });
                                    }}
                                    fullWidth
                                  />
                                </EditableFieldWrapper>
                              </Grid>
                              {isAdmin(user) && (
                                <>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <EditableFieldWrapper
                                      label={t('minAppVersion')}
                                      editing={editingItemIndex === index}
                                      value={item.minVersion}
                                    >
                                      <FormTextField
                                        name={`profileMenu[${index}].minVersion`}
                                        onChange={(e) => {
                                          const updatedMenu = [
                                            ...values.profileMenu,
                                          ];
                                          updatedMenu[index] = {
                                            ...updatedMenu[index],
                                            minVersion: e.target.value,
                                          };
                                          setValues({
                                            profileMenu: updatedMenu,
                                          });
                                        }}
                                        fullWidth
                                        placeholder="e.g., 1.0.0"
                                      />
                                    </EditableFieldWrapper>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <EditableFieldWrapper
                                      label={t('maxAppVersion')}
                                      editing={editingItemIndex === index}
                                      value={item.maxVersion}
                                    >
                                      <FormTextField
                                        name={`profileMenu[${index}].maxVersion`}
                                        onChange={(e) => {
                                          const updatedMenu = [
                                            ...values.profileMenu,
                                          ];
                                          updatedMenu[index] = {
                                            ...updatedMenu[index],
                                            maxVersion: e.target.value,
                                          };
                                          setValues({
                                            profileMenu: updatedMenu,
                                          });
                                        }}
                                        fullWidth
                                        placeholder="e.g., 2.0.0"
                                      />
                                    </EditableFieldWrapper>
                                  </Grid>
                                </>
                              )}
                              <Grid size={{ xs: 12, md: 4 }}>
                                <EditableFieldWrapper
                                  label={t('platform')}
                                  editing={editingItemIndex === index}
                                  value={
                                    platformOptions.find(
                                      (option) =>
                                        option.id === (item.platform || '')
                                    )?.title || 'All'
                                  }
                                >
                                  <Autocomplete
                                    options={platformOptions}
                                    getOptionLabel={(option) => option.title}
                                    value={
                                      platformOptions.find(
                                        (option) =>
                                          option.id === (item.platform || '')
                                      ) || platformOptions[0]
                                    }
                                    onChange={(_, newValue) => {
                                      const updatedMenu = [
                                        ...values.profileMenu,
                                      ];
                                      updatedMenu[index] = {
                                        ...updatedMenu[index],
                                        platform: newValue?.id || '',
                                      };
                                      setValues({
                                        profileMenu: updatedMenu,
                                      });
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth />
                                    )}
                                  />
                                </EditableFieldWrapper>
                              </Grid>
                              {item.visibility?.includes?.('group') && (
                                <Grid size={{ xs: 12, md: 4 }}>
                                  <EditableFieldWrapper
                                    label={t('groups')}
                                    editLabel={t('groupIds')}
                                    editing={editingItemIndex === index}
                                    value={item.groupId?.toString() || ''}
                                  >
                                    <GroupSelect
                                      multiple
                                      value={
                                        item.groupId
                                          ? item.groupId
                                              .toString()
                                              .split(',')
                                              .map((id) =>
                                                parseInt(id.trim(), 10)
                                              )
                                              .filter((id) => id)
                                          : []
                                      }
                                      onChange={(value) => {
                                        const updatedMenu = [
                                          ...values.profileMenu,
                                        ];
                                        updatedMenu[index] = {
                                          ...updatedMenu[index],
                                          groupId: Array.isArray(value)
                                            ? value.join(',')
                                            : value,
                                        };
                                        setValues({
                                          profileMenu: updatedMenu,
                                        });
                                      }}
                                      helperText={t('groupHelperText')}
                                      platformId={platform?.id?.toString()}
                                    />
                                  </EditableFieldWrapper>
                                </Grid>
                              )}
                              <Grid size={{ xs: 12 }}>
                                <EditableFieldWrapper
                                  label={t('visibilityOptions')}
                                  editing={editingItemIndex === index}
                                  value={
                                    item.visibility?.map?.(
                                      (v) =>
                                        tabVisibilityOptions.find(
                                          (opt) => opt.id === v
                                        )?.title || v
                                    ) || []
                                  }
                                >
                                  <FormAutocomplete
                                    multiple
                                    options={tabVisibilityOptions}
                                    name={`profileMenu[${index}].visibility`}
                                    placeholder={t('selectVisibilityOptions')}
                                    filterSelectedOptions
                                    isOptionEqualToValue={(option, value) =>
                                      (typeof option === 'string'
                                        ? option
                                        : option.id) ===
                                      (typeof value === 'string'
                                        ? value
                                        : value.id)
                                    }
                                    getOptionLabel={(option) =>
                                      typeof option === 'string'
                                        ? tabVisibilityOptions.find(
                                            (opt) => opt.id === option
                                          )?.title || option
                                        : option.title
                                    }
                                    transform={(value) => {
                                      // Convert to string IDs and remove duplicates
                                      const uniqueIds = Array.from(
                                        new Set(
                                          value.map((v) =>
                                            typeof v === 'string' ? v : v.id
                                          )
                                        )
                                      );
                                      return uniqueIds;
                                    }}
                                  />
                                </EditableFieldWrapper>
                              </Grid>
                            </Grid>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  }}
                </DndSortableList>

                {/* Add Menu Item Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={
                      <Icon icon={faPlus} />
                    }
                    onClick={() => handleAddMenuItem()}
                  >
                    {t('addMenuItem')}
                  </Button>
                </Box>

                {/* Preset Menu Item Selection Dialog */}
                <Dialog
                  open={isPresetDialogOpen}
                  onClose={() => setIsPresetDialogOpen(false)}
                  maxWidth="md"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      p: 1,
                    },
                  }}
                >
                  <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {t('selectMenuItemTemplate')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {t('chooseTemplateDescription')}
                    </Typography>
                  </DialogTitle>
                  <DialogContent>
                    <Box sx={{ mb: 3, mt: 1 }}>
                      <TextField
                        fullWidth
                        placeholder={t('searchTemplates')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon
                                icon={faSearch}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Grid container spacing={3}>
                      {filteredTabs.map((preset) => (
                        <Grid key={preset.name} size={{ xs: 6, md: 4 }}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() =>
                              handlePresetSelection(preset, {
                                values,
                                setValues,
                              })
                            }
                            sx={{
                              height: '120px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              borderRadius: 2,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 3,
                                borderColor: 'primary.main',
                              },
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Stack spacing={1} alignItems="center">
                              <Typography
                                variant="subtitle1"
                                fontWeight="medium"
                              >
                                {preset.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  display: '-webkit-box',
                                  overflow: 'hidden',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                }}
                              >
                                {preset.description}
                              </Typography>
                            </Stack>
                          </Button>
                        </Grid>
                      ))}

                      {filteredTabs.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Icon
                              icon={faSearch}
                              sx={{
                                fontSize: 40,
                                color: 'text.disabled',
                                mb: 2,
                              }}
                            />
                            <Typography variant="h6" color="text.secondary">
                              {t('noTemplatesMatch')}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                              {t('tryDifferentSearch')}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                      onClick={() => setIsPresetDialogOpen(false)}
                      color="inherit"
                      variant="outlined"
                      startIcon={
                        <Icon icon={faXmark} />
                      }
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={() =>
                        handleAddEmptyMenuItem(values, { setValues })
                      }
                      variant="contained"
                      startIcon={
                        <Icon icon={faPlusSolid} />
                      }
                    >
                      {t('createEmptyItem')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Stack>
            </StickyCard>
          </Form>
        )}
      </Formik>
    </Accordion>
  );
}
